// Shared formatters and pure helpers. SA locale by default.

const ZAR = new Intl.NumberFormat("en-ZA", {
  style: "currency",
  currency: "ZAR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const ZAR_NOCENTS = new Intl.NumberFormat("en-ZA", {
  style: "currency",
  currency: "ZAR",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export function formatRand(amount: number | null | undefined, opts?: { cents?: boolean }) {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return "—";
  const fmt = opts?.cents === false ? ZAR_NOCENTS : ZAR;
  return fmt.format(Number(amount));
}

const SA_DATE_LONG = new Intl.DateTimeFormat("en-ZA", {
  weekday: "short",
  day: "2-digit",
  month: "short",
  year: "numeric",
});

const SA_TIME = new Intl.DateTimeFormat("en-ZA", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
});

export function formatDate(input: string | Date | null | undefined): string {
  if (!input) return "—";
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return "—";
  // SA standard numeric format: DD/MM/YYYY (built manually so the order is
  // guaranteed regardless of the runtime's locale data).
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
}

export function formatDateLong(input: string | Date | null | undefined): string {
  if (!input) return "—";
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return "—";
  return SA_DATE_LONG.format(d);
}

export function formatTime(input: string | Date | null | undefined): string {
  if (!input) return "—";
  if (typeof input === "string" && /^\d{2}:\d{2}/.test(input)) {
    // Plain "HH:MM:SS" or "HH:MM" — wrap in a date for formatting.
    const [h, m] = input.split(":");
    const d = new Date();
    d.setHours(Number(h ?? 0), Number(m ?? 0), 0, 0);
    return SA_TIME.format(d);
  }
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return "—";
  return SA_TIME.format(d);
}

export function timeAgo(input: string | Date | null | undefined): string {
  if (!input) return "—";
  const then = input instanceof Date ? input : new Date(input);
  const seconds = Math.round((Date.now() - then.getTime()) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.round(days / 7)}w ago`;
  if (days < 365) return `${Math.round(days / 30)}mo ago`;
  return `${Math.round(days / 365)}y ago`;
}

export function daysBetween(a: Date | string, b: Date | string): number {
  const dA = a instanceof Date ? a : new Date(a);
  const dB = b instanceof Date ? b : new Date(b);
  return Math.round((dB.getTime() - dA.getTime()) / (24 * 60 * 60 * 1000));
}

export function timeOfDayGreeting(now = new Date()): string {
  const h = now.getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

export function firstName(fullName: string | null | undefined): string {
  if (!fullName) return "there";
  return fullName.trim().split(/\s+/)[0] ?? "there";
}

export function initials(fullName: string | null | undefined): string {
  if (!fullName) return "?";
  return fullName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
}

export function cn(
  ...inputs: Array<string | number | boolean | null | undefined>
): string {
  return inputs
    .filter((v): v is string => typeof v === "string" && v.length > 0)
    .join(" ");
}

export function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

export function currentMonthYear(): { month: number; year: number } {
  const d = new Date();
  return { month: d.getMonth() + 1, year: d.getFullYear() };
}

export function dayKeyFromDate(date: Date): "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun" {
  const keys = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;
  return keys[date.getDay()] as (typeof keys)[number];
}

export function fileExtension(name: string): string {
  return name.split(".").pop()?.toLowerCase() ?? "";
}

export function bytesHuman(bytes: number): string {
  const units = ["B", "KB", "MB", "GB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}
